import { lessonNode, unitStarted } from "@aybuben/client/screens/home.ts";
import { LABEL_GAP, LABEL_W, nodeLine, NODE_R } from "@aybuben/client/screens/trail.ts";
import type { UnitView } from "@aybuben/core/content/course.ts";
import type { LessonDef } from "@aybuben/core/content/types.ts";
import type { GameState } from "@aybuben/core/state.ts";
import { Check, CloudOff, Lock } from "lucide-react";
import { useMemo, type Ref } from "react";

import { useStrings } from "../../i18n/index.tsx";
import { COL, edgeOf, FOCUS } from "../../theme.ts";
import { KIND_UI } from "../lesson-kinds.tsx";

interface TrailNodeProps {
	u: UnitView;
	lesson: LessonDef;
	st: GameState;
	/** 1-based position in the unit: the kind and unit alone repeat within a unit. */
	position: number;
	/** Where the disc's centre sits in the trail. */
	x: number;
	y: number;
	isCurrent: boolean;
	/** The trail walked a new stretch this render: the ring fades in. */
	ringIn: boolean;
	/** How many segments filled in before this ring: it fades in once they have. */
	ringDelay: number;
	offline: boolean;
	/** On the wrapper around the button: focus and the jump button scroll to it. */
	focusRef?: Ref<HTMLDivElement>;
	currentRef?: Ref<HTMLDivElement>;
	onClick: () => void;
}

export function TrailNode({
	u,
	lesson,
	st,
	position,
	x,
	y,
	isCurrent,
	ringIn,
	ringDelay,
	offline,
	focusRef,
	currentRef,
	onClick,
}: TrailNodeProps) {
	const t = useStrings();
	const { state, open, blocked, times, ariaKey } = lessonNode(st, u, lesson, offline);
	const { icon: Icon, nameKey } = KIND_UI[lesson.kind];
	const live = open && !blocked;
	const fill = !live ? COL.grey : times > 0 ? COL.apricot : COL.pom;
	const lip = !live ? COL.greyEdge : edgeOf(fill);
	const { key: lineKey, current } = nodeLine(state, isCurrent, unitStarted(st, u));
	const line = t(lineKey, { n: times });
	// Both refs may name this node; one stable callback sets them all.
	const setRefs = useMemo(
		() => (el: HTMLDivElement | null) => {
			for (const r of [focusRef, currentRef]) {
				if (typeof r === "function") r(el);
				// A RefObject's current is writable; the Ref type only hides that.
				else if (r) (r as { current: HTMLDivElement | null }).current = el;
			}
		},
		[focusRef, currentRef],
	);
	return (
		<div
			ref={setRefs}
			className="absolute"
			style={{ left: x - NODE_R, top: y - NODE_R, width: 2 * NODE_R + LABEL_GAP + LABEL_W }}
		>
			<button
				onClick={onClick}
				aria-label={t(ariaKey, {
					kind: t(nameKey),
					n: u.number,
					i: position,
					count: u.def.lessons.length,
					title: u.title,
					times,
				})}
				className={`flex w-full items-center text-left transition active:translate-y-1 ${FOCUS}`}
				style={{ gap: LABEL_GAP, minHeight: 2 * NODE_R, borderRadius: NODE_R }}
			>
				<span className="relative shrink-0" style={{ width: 2 * NODE_R, height: 2 * NODE_R }}>
					{current && (
						<span
							data-trail-ring
							className={`absolute inset-0 rounded-full ${ringIn ? "motion-ring" : ""}`}
							style={{
								boxShadow: `0 0 0 7px color-mix(in oklab, ${COL.pom} 22%, transparent)`,
								...(ringIn ? { animationDelay: `${ringDelay}ms` } : {}),
							}}
						/>
					)}
					<span
						className="relative flex h-full w-full items-center justify-center rounded-full"
						style={{
							background: fill,
							boxShadow: `0 4px 0 ${lip}`,
							color: live ? "#fff" : COL.greyDark,
						}}
					>
						{!open ? (
							<Lock size={20} />
						) : blocked ? (
							<CloudOff size={20} />
						) : times > 0 ? (
							<Check size={24} strokeWidth={3} />
						) : (
							<Icon size={24} />
						)}
					</span>
				</span>
				<span className="flex min-w-0 flex-col" style={{ maxWidth: LABEL_W }}>
					<span
						className="text-sm leading-tight font-extrabold"
						style={{ color: live ? COL.ink : COL.faint }}
					>
						{t(nameKey)}
					</span>
					<span
						{...(current ? { "data-current-line": "" } : {})}
						className={`text-xs leading-tight ${current ? "font-extrabold" : ""}`}
						style={{ color: current ? COL.pom : COL.mute }}
					>
						{line}
					</span>
				</span>
			</button>
		</div>
	);
}
